import { Button, DatePicker, Table } from "antd";
import React, { useEffect, useState, useCallback } from "react";
import type { ColumnsType } from "antd/es/table";
import { DownloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { InstallmentTraceReportDTO } from "../../../model/models";
import { HttpService } from "../../../services/http/Http.service";
import { HttpErrorService } from "../../../services/http/HttpError.service";
import { UtilsService } from "../../../services/Utils.service";
import { PracticeExportService } from "../../practices/PracticeExport.service";

// Struttura di risposta paginata da Spring
interface SpringPage<T> {
    content: T[];
    totalElements: number;
    number: number; // 0-based page index
    size: number;
}

export default function ReportIncassi() {
    // Stati
    const [data, setData] = useState<InstallmentTraceReportDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingReport, setLoadingReport] = useState(false);
    const [fromDate, setFromDate] = useState<string | null>(null);
    const [toDate, setToDate] = useState<string | null>(null);
    const [pagination, setPagination] = useState({
        current: 1, // Ant Design è 1-based
        pageSize: 20,
        total: 0,
    });

    // Definizione dei tipi locali
    type Row = InstallmentTraceReportDTO;

    const toNum = (v: unknown): number =>
        typeof v === "number" ? v : v == null ? 0 : Number(v);

    const isHeir = (r: Row) =>
        Boolean(r.heirFiscalCode) || Boolean(r.heirLastName) || Boolean(r.heirFirstName);

    // 1. FUNZIONE DI NORMALIZZAZIONE (SPOSTATA PRIMA DI FETCHDATA)
    const normalizeHeirRows = useCallback((rows: Row[]): Row[] => {
        const groups = new Map<string, Row[]>();
        rows.forEach((r) => {
            const key = `${r.archiveProtocol ?? ""}__${r.fiscalCode ?? ""}`;
            const bucket = groups.get(key);
            if (bucket) bucket.push(r);
            else groups.set(key, [r]);
        });

        const pickExemption = (grp: Row[]): string => {
            const owner = grp.find(
                (r) => !isHeir(r) && r.exemptionCodes && r.exemptionCodes.trim() !== ""
            );
            if (owner?.exemptionCodes) return owner.exemptionCodes;
            const any = grp.find((r) => r.exemptionCodes && r.exemptionCodes.trim() !== "");
            return any?.exemptionCodes ?? "";
        };

        const result: Row[] = [];

        groups.forEach((grp) => {
            const baseInteressi = grp.reduce<number>(
                (acc: number, r: Row) => Math.max(acc, toNum(r.interessi)),
                0
            );
            const baseSpese = grp.reduce<number>(
                (acc: number, r: Row) => Math.max(acc, toNum(r.spese)),
                0
            );
            const baseExemption = pickExemption(grp);

            grp.forEach((r) => {
                const row: Row = { ...r };

                if (isHeir(row)) {
                    row.interessi = baseInteressi;
                    row.spese = baseSpese;

                    if (!row.exemptionCodes || row.exemptionCodes.trim() === "") {
                        row.exemptionCodes = baseExemption;
                    }

                    row.totale =
                        toNum(row.ticket) + toNum(row.sanzione) + baseInteressi + baseSpese;
                }

                result.push(row);
            });
        });

        return result;
    }, []); // Non ha dipendenze esterne allo scope della funzione

    // 2. FUNZIONE DI FETCHING (ORA DEFINITA DOPO LA DIPENDENZA)
    const fetchData = useCallback(
        async (
            page: number,
            size: number,
            from: string | null, // Usiamo lo stato attuale del componente
            to: string | null
        ) => {
            setLoading(true);
            try {
                const fromDateTime = from ? dayjs(from).startOf("day").toISOString() : undefined;
                const toDateTime = to ? dayjs(to).endOf("day").toISOString() : undefined;

                let url = `/installment/report-tracciato`;
                const params = new URLSearchParams();

                if (fromDateTime) params.append('from', fromDateTime);
                if (toDateTime) params.append('to', toDateTime);

                // Spring è 0-based
                params.append('page', (page - 1).toString());
                params.append('size', size.toString());

                if (params.toString()) url += `?${params.toString()}`;

                const res = await HttpService.getInstanceAxios().get<SpringPage<InstallmentTraceReportDTO>>(url);

                const reportData = res.data.content;
                const totalElements = res.data.totalElements;

                const fixed = normalizeHeirRows(reportData);
                setData(fixed);

                setPagination((p) => ({
                    ...p,
                    total: totalElements,
                    current: res.data.number + 1, // Ant Design è 1-based
                    pageSize: res.data.size,
                }));

            } catch (error: any) {
                HttpErrorService.handle("Errore durante il recupero dei dati del report incassi", error, true);
            } finally {
                setLoading(false);
            }
        },
        [normalizeHeirRows] // Dipendenza ora definita correttamente
    );

    // Funzione handler per il cambio pagina/dimensione
    const handleTableChange = (newPagination: { current: number; pageSize: number }) => {
        // Aggiorna lo stato, e lancia il fetch.
        // NON aggiorniamo qui lo stato di paginazione con setPagination,
        // lo facciamo solo dopo il successo del fetchData per evitare che l'utente veda
        // il cambio pagina prima che i dati siano arrivati.

        fetchData(
            newPagination.current,
            newPagination.pageSize,
            fromDate,
            toDate
        );
    };



    // useEffect per filtri data: Reagisce al cambio di fromDate/toDate
    useEffect(() => {
        if (fromDate !== null || toDate !== null) {
            // Se i filtri cambiano, partiamo sempre dalla prima pagina
            const newPage = 1;

            // Chiamiamo fetchData forzando la pagina 1
            fetchData(newPage, pagination.pageSize, fromDate, toDate);

            // Reset dello stato della pagina, da fare in modo asincrono, ma è meglio farlo
            // nel fetchData dopo che la chiamata è andata a buon fine per sincronia.
            // Tuttavia, per una UX immediata, possiamo aggiornare qui per vedere la pagina "1" attiva
            // prima che i dati arrivino.
            setPagination((p) => ({ ...p, current: newPage }));
        } else {
            // Se i filtri sono stati rimossi (entrambi null), ricarica dalla pagina 1
            const newPage = 1;
            fetchData(newPage, pagination.pageSize, fromDate, toDate);
            setPagination((p) => ({ ...p, current: newPage }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromDate, toDate]);


    const euro = (v: number | null | undefined) =>
        v == null ? "" : `€ ${UtilsService.formatNumber(v)}`;

    const columns: ColumnsType<InstallmentTraceReportDTO> = [
        // ... (Colonne invariate)
        { title: "Anno/i di controllo", dataIndex: "years", key: "years", width: 'auto' },
        { title: "Tipologia", dataIndex: "tipologia", key: "tipologia", width: 'auto' },
        { title: "Codice fiscale", dataIndex: "fiscalCode", key: "fiscalCode", width: 'auto' },
        { title: "Cognome", dataIndex: "lastName", key: "lastName", width: 'auto' },
        { title: "Nome", dataIndex: "firstName", key: "firstName", width: 'auto' },
        { title: "Cod. esenzione", dataIndex: "exemptionCodes", key: "exemptionCodes", width: 'auto' },
        { title: "Protocollo archiviazione", dataIndex: "archiveProtocol", key: "archiveProtocol", width: 'auto' },


        { title: "Ticket", dataIndex: "ticket", key: "ticket", width: 120, render: euro, className: "text-right" },
        { title: "Interessi", dataIndex: "interessi", key: "interessi", width: 120, render: euro, className: "text-right" },
        { title: "Sanzione", dataIndex: "sanzione", key: "sanzione", width: 120, render: euro, className: "text-right" },
        { title: "Spese", dataIndex: "spese", key: "spese", width: 120, render: euro , className: "text-right"},
        { title: "Totale", dataIndex: "totale", key: "totale", width: 130, render: euro, className: "text-right" },


        { title: "Variazione (SI/NO)", dataIndex: "variazioneSiNo", key: "variazioneSiNo", width: 'auto' },
        { title: "Rateizzazione (SI/NO)", dataIndex: "rateizzazioneSiNo", key: "rateizzazioneSiNo", width: 'auto' },
        {
            title: "N. RATE",
            dataIndex: "numRate",
            key: "numRate",
            width: 'auto',
            render: (value: string, row) => (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span>{value}</span>
{/*                    {row.mancatoPagamentoUnico && (
                        <Tooltip title="Mancato pagamento">
                            <Tag color="red" style={{ marginInlineStart: 0 }}>
                                MP
                            </Tag>
                        </Tooltip>
                    )}*/}
                </div>
            ),
        },


        { title: "Ticket", dataIndex: "ticketRata", key: "ticketRata", width: 'auto', render: euro, className: "text-right" },
        { title: "Interessi", dataIndex: "interessiRata", key: "interessiRata", width: 'auto', render: euro, className: "text-right" },
        { title: "Sanzione", dataIndex: "sanzioneRata", key: "sanzioneRata", width: 'auto', render: euro, className: "text-right" },
        { title: "Spese", dataIndex: "speseRata", key: "speseRata", width: 'auto', render: euro, className: "text-right" },
        { title: "Totale", dataIndex: "totaleRata", key: "totaleRata", width: 'auto', render: euro, className: "text-right" },


        { title: "Importo pagato", dataIndex: "importoPagato", key: "importoPagato", width: 'auto', render: euro, className: "text-right" },
        {
            title: "Data pagamento",
            dataIndex: "dataPagamento",
            key: "dataPagamento",
            width: 'auto',
            render: (v: string) => (v ? UtilsService.formatDateString(v) : "")
        },
        { title: "Tipo pagamento", dataIndex: "metodoPagamento", key: "metodoPagamento", width: 'auto' },
        { title: "Numero pagamento", dataIndex: "numeroPagamento", key: "numeroPagamento", width: 'auto' },
        { title: "Causale versamento", dataIndex: "causaleVersamento", key: "causaleVersamento", width: 'auto' },
        { title: "Tipo dovuto", dataIndex: "tipoDovuto", key: "tipoDovuto", width: 'auto' },

        // eredi (se presenti)
        { title: "Codice fiscale EREDE", dataIndex: "heirFiscalCode", key: "heirFiscalCode", width: 'auto' },
        { title: "Cognome EREDE", dataIndex: "heirLastName", key: "heirLastName", width: 'auto' },
        { title: "Nome EREDE", dataIndex: "heirFirstName", key: "heirFirstName", width: 'auto' },
    ];


    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 16, display: "flex", gap: 8, alignItems: "center" }}>
                <DatePicker
                    format={"DD-MM-YYYY"}
                    placeholder="Da"
                    value={fromDate ? dayjs(fromDate) : null}
                    onChange={(d) => setFromDate(d ? d.format("YYYY-MM-DD") : null)}
                />
                <DatePicker
                    format={"DD-MM-YYYY"}
                    placeholder="A"
                    value={toDate ? dayjs(toDate) : null}
                    onChange={(d) => setToDate(d ? d.format("YYYY-MM-DD") : null)}
                />
                <Button loading={loadingReport} disabled={loadingReport || !fromDate || !toDate }
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={async () => {
                        setLoadingReport(true);
                        if (fromDate && toDate) {
                            await PracticeExportService.loadAndExportToExcelTrace(fromDate, toDate, "report_incassi.xlsx");
                        } else {
                            // fallback: no filters => fetch all
                            await PracticeExportService.loadAndExportToExcelTrace(null, null, "report_incassi.xlsx");
                        }
                        setLoadingReport(false);
                    }}
                >
                    Genera report
                </Button>

            </div>

            <Table
                rowKey={(r) => String(r.rataId)}
                columns={columns}
                dataSource={data}
                loading={loading}
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    // Gestione del cambio pagina/size: usa il nuovo handler
                    onChange: (page, pageSize) => handleTableChange({ current: page, pageSize }),
                    // Quando cambia la dimensione, resetta la pagina a 1
                    onShowSizeChange: (current, size) => handleTableChange({ current: 1, pageSize: size }),
                }}
                bordered
                size="small"
                scroll={{ x: "max-content" }}
            />

        </div>
    );
}
